<script>
import store_delete from "@/mixins/store-delete";
import { apiService } from "@/_services";
import { mapGetters } from "vuex";
import _merge from "lodash/merge";
import _isEqual from "lodash/isEqual";
import _findIndex from "lodash/findIndex";

export default {
  name: "store-form",

  mixins: [store_delete],

  props: {
    propID: {
      default: 0,
      type: Number
    }
  },

  data() {
    return {
      id: this.propID,
      newData: {}
    };
  },

  created() {
    this.setup();
  },

  computed: {
    ...mapGetters("stores", ["brands", "zones", "programs"]),
    currentData() {
      if (!this.id) return {};
      return this.$store.getters["stores/getEntryByID"](this.id) || {};
    },
    saveNeeded() {
      return !_isEqual(this.currentData, this.newData);
    },
    saveButtonText() {
      if (this.id) {
        return this.saveNeeded ? "Save changes" : "No changes";
      }
      return "Insert";
    },
    index() {
      if (this.id) {
        let entries = this.$store.getters["stores/entries"];
        let index = _findIndex(entries, { ID: this.id });
        return index;
      }
      return null;
    }
  },

  methods: {
    setup() {
      this.newData = _merge({}, this.currentData);
    },
    deleteEntry() {
      this.deletePrompt(this.id).then(response => {
        if (response) this.$parent.close();
      });
    },
    submit() {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      let payload = _merge({}, this.newData);
      let isUpdate = {}.hasOwnProperty.call(payload, "ID");
      let toastMsg = isUpdate ? "Store updated!" : "Store created";
      apiService.stores
        .insert(payload)
        .then(response => {
          if (
            {}.hasOwnProperty.call(response, "ID") &&
            this.id !== response.ID
          ) {
            this.id = response.ID;
          }
          setTimeout(() => {
            this.$buefy.toast.open(toastMsg);
          }, 100);
        })
        .finally(() => {
          loadingComponent.close();
        });
    }
  },

  watch: {
    currentData: {
      deep: true,
      handler() {
        this.setup();
      }
    }
  }
};
</script>

<template>
  <div id="store-form" class="modal-card" style="width: auto">
    <header class="modal-card-head">
      <p class="modal-card-title" v-if="id">Editing Store</p>
      <p class="modal-card-title" v-else>New Store</p>
    </header>
    <section class="modal-card-body">
      <form @submit.prevent="submit">
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label" for="i--name">Name</label>
              <div class="control is-clearfix">
                <input class="input" id="i--name" v-model="newData.name" />
              </div>
            </div>
          </div>
        </div>
        <div class="columns is-mobile">
          <div class="column">
            <div class="field">
              <label class="label" for="i--brand">Brand</label>
              <div class="control is-expanded">
                <span class="select is-fullwidth">
                  <select name="i--brand" id="i--brand" v-model="newData.brand">
                    <option v-for="brand in brands" :key="brand" :value="brand">
                      {{ brand | formatSlug }}
                    </option>
                  </select>
                </span>
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label" for="i--zone">Zone</label>
              <div class="control is-expanded">
                <span class="select is-fullwidth">
                  <select name="i--zone" id="i--zone" v-model="newData.zone">
                    <option v-for="zone in zones" :key="zone" :value="zone">
                      {{ zone | formatSlug }}
                    </option>
                  </select>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label" for="i--program">Program</label>
              <div class="control is-expanded">
                <span class="select is-fullwidth">
                  <select
                    name="i--program"
                    id="i--program"
                    v-model="newData.program"
                  >
                    <option value=""></option>
                    <option
                      v-for="program in programs"
                      :key="program"
                      :value="program"
                    >
                      {{ program | formatSlug }}
                    </option>
                  </select>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label" for="i--address">Address</label>
              <div class="control is-clearfix">
                <input
                  class="input"
                  id="i--address"
                  v-model="newData.address"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="columns is-mobile">
          <div class="column">
            <div class="field">
              <label class="label" for="i--latitude">Latitude</label>
              <div class="control is-clearfix">
                <input
                  class="input"
                  id="i--latitude"
                  type="number"
                  v-model.number="newData.latitude"
                />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label" for="i--longitude">Longitude</label>
              <div class="control is-clearfix">
                <input
                  class="input"
                  id="i--longitude"
                  type="number"
                  v-model.number="newData.longitude"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </section>
    <footer class="modal-card-foot">
      <button v-if="id" class="button" type="button" @click="$parent.close()">
        Close
      </button>
      <button v-else class="button" type="button" @click="$parent.close()">
        Cancel
      </button>
      <b-button v-if="id" type="is-danger" @click="deleteEntry()">
        Delete
      </b-button>
      <b-button
        @click="submit"
        type="is-primary"
        :disabled="!saveNeeded"
        v-text="saveButtonText"
      ></b-button>
    </footer>
  </div>
</template>

<style lang="scss">
#store-form {
  // max-width: 40rem;
  form {
    *,
    input {
      font-size: 0.875rem;
    }
  }
}
</style>
